import React, { useState, useEffect, useRef } from 'react';
import { adminAPI } from '../../../services/api';
import toast from 'react-hot-toast';
import CodeEditor from '../../../components/common/CodeEditor';

const TemplateManager = ({ templates, onUpload }) => {
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [uploading, setUploading] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const fileInputRef = useRef(null);
  const [variables, setVariables] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); // For delete confirmation
  const [templateToDelete, setTemplateToDelete] = useState(null); // Template to delete
  const [deletingId, setDeletingId] = useState(null);
  // ... existing code ...

  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;
    
    try {
      setDeletingId(templateToDelete.id);
      await adminAPI.deleteTemplate(templateToDelete.id);
      
      toast.success('Template deleted successfully');
      
      // If the deleted template was active, clear the form
      if (activeTemplate?.id === templateToDelete.id) {
        setActiveTemplate(null);
        setHtmlContent('');
        setTemplateName('');
        setTemplateDescription('');
        setIsDefault(false);
      }
      
      // Refresh templates list (you might want to pass a callback or use state)
      // For now, we'll reload the window or you can add a refresh function
      window.location.reload(); // Or use a better state management approach
      
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error(error.detail || 'Failed to delete template');
    } finally {
      setDeletingId(null);
      setShowDeleteConfirm(false);
      setTemplateToDelete(null);
    }
  };

   const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setTemplateToDelete(null);
  };

  // Sample data for preview
  const sampleData = {
    student: {
      name: "John Doe",
      reg_no: "STU2023001",
      class: "10A",
      gender: "Male",
      dob: "15/05/2008",
      age: 15
    },
    session: {
      name: "2023/2024",
      id: 1
    },
    term: {
      name: "First Term",
      id: 1
    },
    subjects: [
      { name: "Mathematics", ca_score: 28, exam_score: 65, total: 93, grade: "A", remark: "Excellent", position: 1 },
      { name: "English", ca_score: 25, exam_score: 60, total: 85, grade: "AB", remark: "Very Good", position: 3 },
      { name: "Physics", ca_score: 24, exam_score: 58, total: 82, grade: "B", remark: "Good", position: 5 }
    ],
    summary: {
      total_score: 260,
      average_score: 86.67,
      overall_grade: "A",
      class_position: "2nd",
      teacher_comment: "Excellent performance. Keep up the good work!",
      total_subjects: 3,
      report_date: "15 December, 2023"
    },
    school: {
      name: "SCHOOL OF EXCELLENCE",
      address: "123 Education Street, Knowledge City",
      phone: "+234 123 456 7890",
      email: "info@schoolexcellence.edu",
      motto: "Knowledge is Power"
    }
  };

  // Available template variables for guidance
  const availableVariables = [
    { name: "student.name", description: "Student full name", example: "John Doe" },
    { name: "student.reg_no", description: "Registration number", example: "STU2023001" },
    { name: "student.class", description: "Class name", example: "10A" },
    { name: "student.gender", description: "Gender", example: "Male" },
    { name: "student.dob", description: "Date of birth", example: "15/05/2008" },
    { name: "student.age", description: "Age", example: "15" },
    { name: "session.name", description: "Academic session", example: "2023/2024" },
    { name: "term.name", description: "Term", example: "First Term" },
    { name: "subjects", description: "List of subjects (use {% for %})", example: "Loop through subjects" },
    { name: "subject.name", description: "Subject name", example: "Mathematics" },
    { name: "subject.ca_score", description: "CA score", example: "28" },
    { name: "subject.exam_score", description: "Exam score", example: "65" },
    { name: "subject.total", description: "Total score", example: "93" },
    { name: "subject.grade", description: "Grade", example: "A" },
    { name: "subject.remark", description: "Remark", example: "Excellent" },
    { name: "subject.position", description: "Position in subject", example: "1" },
    { name: "summary.total_score", description: "Total score", example: "260" },
    { name: "summary.average_score", description: "Average score", example: "86.67" },
    { name: "summary.overall_grade", description: "Overall grade", example: "A" },
    { name: "summary.class_position", description: "Position in class", example: "2nd" },
    { name: "summary.teacher_comment", description: "Teacher's comment", example: "Excellent..." },
    { name: "summary.total_subjects", description: "Number of subjects", example: "3" },
    { name: "summary.report_date", description: "Report generation date", example: "15 December, 2023" },
    { name: "school.name", description: "School name", example: "SCHOOL OF EXCELLENCE" },
    { name: "school.address", description: "School address", example: "123 Education Street..." },
    { name: "school.phone", description: "School phone", example: "+234 123 456 7890" },
    { name: "school.email", description: "School email", example: "info@school.edu" },
    { name: "school.motto", description: "School motto", example: "Knowledge is Power" }
  ];

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.html')) {
      toast.error('Please upload an HTML file');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const content = e.target.result;
      setHtmlContent(content);
      
      // Auto-extract template name from filename
      const name = file.name.replace('.html', '').replace(/_/g, ' ');
      setTemplateName(name);
    };
    reader.readAsText(file);
  };

  
  


  // TemplateManager.jsx

const handlePreview = async () => {
  if (!htmlContent) {
    toast.error('Please add HTML content first');
    return;
  }

  try {
    // Create FormData for preview
    const formData = new FormData();
    formData.append('html', htmlContent);
    formData.append('data', JSON.stringify(sampleData));
    
    const response = await adminAPI.previewTemplate(formData);
    setPreviewHtml(response.data.html); // Fixed: access response.data.html
  } catch (error) {
    console.log(error);
    toast.error('Failed to preview template');
  }
};

// const handleSaveTemplate = async () => {
//   if (!templateName || !htmlContent) {
//     toast.error('Template name and content are required');
//     return;
//   }

//   try {
//     setUploading(true);
    
//     const formData = new FormData();
//     formData.append('name', templateName);
//     formData.append('description', templateDescription);
//     formData.append('is_default', isDefault);
    
//     // Create a blob file from HTML content
//     const blob = new Blob([htmlContent], { type: 'text/html' });
//     const file = new File([blob], `${templateName.replace(/\s+/g, '_')}.html`, { type: 'text/html' });
//     formData.append('file', file);

//     await onUpload(formData);
    
//     // Reset form
//     setTemplateName('');
//     setTemplateDescription('');
//     setHtmlContent('');
//     setIsDefault(false);
//     setPreviewHtml('');
    
//     toast.success('Template saved successfully');
//   } catch (error) {
//     toast.error('Failed to save template');
//   } finally {
//     setUploading(false);
//   }
// };



const handleSaveTemplate = async () => {
  if (!templateName || !htmlContent) {
    toast.error('Template name and content are required');
    return;
  }

  try {
    setUploading(true);
    
    const formData = new FormData();
    formData.append('name', templateName);
    formData.append('description', templateDescription);
    formData.append('is_default', isDefault);
    
    // Create a blob file from HTML content
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const file = new File([blob], `${templateName.replace(/\s+/g, '_')}.html`, { type: 'text/html' });
    formData.append('file', file);

    const response = await onUpload(formData);
    
    // Reset form
    setTemplateName('');
    setTemplateDescription('');
    setHtmlContent('');
    setIsDefault(false);
    setPreviewHtml('');
    
    toast.success('Template saved successfully');
    
    // Refresh templates list if needed
    // You might want to call a function to refresh templates here
    
  } catch (error) {
    console.error('Failed to save template:', error);
    toast.error(error.response?.data?.detail || 'Failed to save template');
  } finally {
    setUploading(false);
  }
};

  // const handleTemplateClick = (template) => {
  //   setActiveTemplate(template);
  //   // In a real app, you'd fetch the template HTML from the server
  //   setHtmlContent(template.content || '');
  //   setTemplateName(template.name);
  //   setTemplateDescription(template.description || '');
  //   setIsDefault(template.is_default || false);
  // };

  const handleTemplateClick = (template) => {
  setActiveTemplate(template);
  // Use template.content instead of template.html_content
  setHtmlContent(template.content || template.html_content || '');
  setTemplateName(template.name);
  setTemplateDescription(template.description || '');
  setIsDefault(template.is_default || false);
};

  const extractVariables = (html) => {
    // Simple regex to find {{ variable }} patterns
    const regex = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}/g;
    const matches = html.match(regex) || [];
    const uniqueVars = [...new Set(matches.map(match => match.replace(/\{\{\s*|\s*\}\}/g, '')))];
    setVariables(uniqueVars);
  };

  useEffect(() => {
    if (htmlContent) {
      extractVariables(htmlContent);
    }
  }, [htmlContent]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Template List & Form */}
        <div className="space-y-6">
          {/* Template List */}
          {/* <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="text-lg font-semibold">Available Templates</h3>
              <p className="text-sm text-gray-500">Select a template to edit or create new</p>
            </div>
            <div className="p-4">
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    className={`p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                      activeTemplate?.id === template.id ? 'bg-blue-50 border-blue-200' : ''
                    }`}
                    onClick={() => handleTemplateClick(template)}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium">{template.name}</h4>
                        {template.description && (
                          <p className="text-sm text-gray-500 truncate">{template.description}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {template.is_default && (
                          <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                            Default
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          {new Date(template.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                
                {templates.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <i className="fas fa-file-code text-3xl mb-3 opacity-50"></i>
                    <p>No templates yet. Upload your first template!</p>
                  </div>
                )}
              </div>
            </div>
          </div> */}


          {/* Template List - Updated with delete button */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Available Templates</h3>
          <p className="text-sm text-gray-500">Select a template to edit or create new</p>
        </div>
        <div className="p-4">
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {templates.map((template) => (
              <div
                key={template.id}
                className={`p-3 border rounded-lg hover:bg-gray-50 ${
                  activeTemplate?.id === template.id ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                <div className="flex justify-between items-center">
                  <div 
                    className="flex-1 cursor-pointer"
                    onClick={() => handleTemplateClick(template)}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium">{template.name}</h4>
                        {template.description && (
                          <p className="text-sm text-gray-500 truncate">{template.description}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {template.is_default && (
                          <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                            Default
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          {new Date(template.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="ml-3 flex items-center">
                    <button
                      onClick={() => handleDeleteClick(template)}
                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                      title="Delete template"
                      disabled={deletingId === template.id}
                    >
                      {deletingId === template.id ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fas fa-trash"></i>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
            
            {templates.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <i className="fas fa-file-code text-3xl mb-3 opacity-50"></i>
                <p>No templates yet. Upload your first template!</p>
              </div>
            )}
          </div>
        </div>
      </div>


          {/* Upload Form */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="text-lg font-semibold">Upload New Template</h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload HTML File
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept=".html"
                    className="hidden"
                  />
                  <i className="fas fa-file-upload text-3xl text-gray-400 mb-3"></i>
                  <p className="text-sm text-gray-600 mb-2">
                    Drag & drop HTML file or click to browse
                  </p>
                  <button
                    onClick={() => fileInputRef.current.click()}
                    className="btn-primary py-2 px-4"
                  >
                    Browse Files
                  </button>
                  <p className="text-xs text-gray-500 mt-2">
                    Only HTML files are allowed. Max size: 5MB
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="e.g., Default Report Card"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Describe this template..."
                  rows="2"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="default-template"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  className="h-4 w-4 text-primary-600"
                />
                <label htmlFor="default-template" className="ml-2 text-sm text-gray-700">
                  Set as default template for reports
                </label>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleSaveTemplate}
                  disabled={uploading || !templateName || !htmlContent}
                  className={`btn-primary flex-1 ${
                    uploading || !templateName || !htmlContent ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {uploading ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>
                      Save Template
                    </>
                  )}
                </button>
                <button
                  onClick={handlePreview}
                  disabled={!htmlContent}
                  className={`btn-secondary ${
                    !htmlContent ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <i className="fas fa-eye mr-2"></i>
                  Preview
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Template Editor & Preview */}
        <div className="space-y-6">
          {/* Template Variables Guide */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="text-lg font-semibold">Template Variables</h3>
              <p className="text-sm text-gray-500">Use these variables in your HTML template</p>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
                {availableVariables.map((variable, index) => (
                  <div key={index} className="border rounded p-3 hover:bg-gray-50">
                    <div className="font-mono text-sm bg-gray-100 px-2 py-1 rounded mb-1">
                      { variable.name }
                    </div>
                    <p className="text-sm text-gray-600">{variable.description}</p>
                    <p className="text-xs text-gray-500 mt-1">Example: {variable.example}</p>
                  </div>
                ))}
              </div>
              
              {variables.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="font-medium mb-2">Variables detected in your template:</h4>
                  <div className="flex flex-wrap gap-2">
                    {variables.map((variable, index) => (
                      <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded">
                        {variable}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* HTML Editor */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">HTML Template Editor</h3>
              <div className="text-sm text-gray-500">
                {htmlContent.length} characters
              </div>
            </div>
            {/*  */}
          
<div className="p-1">
  <CodeEditor
    value={htmlContent}
    onChange={setHtmlContent}
    language="html"
    height="300px"
    placeholder="<!DOCTYPE html>
<html>
<head>
    <title>Report Template</title>
    <style>
        /* Add your CSS here */
    </style>
</head>
<body>
    <h1>{{ student.name }}</h1>
    <!-- Your template content -->
</body>
</html>"
  />
</div>
          </div>

          {/* Preview Panel */}
          {previewHtml && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="text-lg font-semibold">Live Preview</h3>
                <button
                  onClick={() => setPreviewHtml('')}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="p-4">
                <div className="border rounded-lg p-4 bg-gray-50">
                  <div 
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                </div>
                <div className="mt-4 text-center">
                  <button
                    onClick={() => {
                      const win = window.open();
                      win.document.write(previewHtml);
                      win.document.close();
                    }}
                    className="btn-secondary"
                  >
                    <i className="fas fa-external-link-alt mr-2"></i>
                    Open in New Window
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* bbb */}


        {showDeleteConfirm && templateToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <i className="fas fa-exclamation-triangle text-red-600 text-xl"></i>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete Template
                </h3>
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete this template?
                </p>
              </div>
            </div>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <i className="fas fa-exclamation-circle text-yellow-400"></i>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    {templateToDelete.is_default && 
                      "This is the default template. Deleting it will set another template as default."}
                    {!templateToDelete.is_default && 
                      "This action cannot be undone. The template will be permanently removed."}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancelDelete}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={deletingId === templateToDelete.id}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                disabled={deletingId === templateToDelete.id}
              >
                {deletingId === templateToDelete.id ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Deleting...
                  </>
                ) : (
                  'Delete Template'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* bbb */}

      {/* Template Usage Instructions */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Template Guidelines</h3>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border rounded-lg p-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                <i className="fas fa-code text-blue-600"></i>
              </div>
              <h4 className="font-medium mb-2">HTML Structure</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Use proper HTML5 doctype</li>
                <li>• Include CSS in &lt;style&gt; tags</li>
                <li>• Add &lt;body&gt; content for the report</li>
                <li>• Use tables for structured data</li>
              </ul>
            </div>
            
            <div className="border rounded-lg p-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                <i className="fas fa-variable text-green-600"></i>
              </div>
              <h4 className="font-medium mb-2">Template Variables</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Use {"{{ variable }}"} for single values</li>
                <li>• Use {"{% for %}"} loops for lists</li>
                <li>• Use {"{% if %}"} for conditional logic</li>
                <li>• Access nested objects with dot notation</li>
              </ul>
            </div>
            
            <div className="border rounded-lg p-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
                <i className="fas fa-print text-purple-600"></i>
              </div>
              <h4 className="font-medium mb-2">Print Optimization</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Use A4 page dimensions (210mm × 297mm)</li>
                <li>• Include @media print styles</li>
                <li>• Use inches or millimeters for print</li>
                <li>• Avoid large images for faster PDFs</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateManager;